import cloneDeep from "lodash.clonedeep";
import { AxisDomainItem } from "recharts/types/util/types";

import { IgGrayPrimitives } from "@intelligems/ig-theme";
import { ExperienceCategory } from "@intelligems/ig-types";
import { DashboardPageName } from "@src/api/types/dashboards";
import {
  currencyFormatter,
  noDecimalNumberFormatter,
} from "@src/features/analytics/dataFormatters";
import {
  DataType,
  GraphConfig,
  GraphType,
} from "@src/features/analytics/types";
import { theme } from "@src/theme";
import type { BarMetricData, StackedBarMetricData } from "@src/types/analytics";

export const getTextWidth = function (
  isImpactGraph: boolean,
  testGroupLength: number,
  baseWidth: number
) {
  if (isImpactGraph && testGroupLength <= 3) {
    return baseWidth;
  } else if (testGroupLength <= 2) {
    return baseWidth;
  } else if (testGroupLength <= 3) {
    return 65;
  } else if (testGroupLength <= 5) {
    return 55;
  } else return 70;
};

export const getImpact = function (
  groupMetricData: { id: string; value: any; plus_minus?: number | null }[],
  compareGroupA: string,
  compareGroupB: string,
  showUplift?: boolean,
  locale?: string
) {
  const indexA = groupMetricData.findIndex(
    (group: BarMetricData) => group.id === compareGroupA
  );
  const indexB = groupMetricData.findIndex(
    (group: BarMetricData) => group.id === compareGroupB
  );

  if (groupMetricData[indexB]?.value && groupMetricData[indexA]?.value) {
    const impact = calculateImpact(
      groupMetricData[indexB].value,
      groupMetricData[indexA].value,
      locale
    );
    const uplift = showUplift ? groupMetricData[indexB]?.plus_minus : null;
    if (parseInt(impact) === 0) {
      return { impact: "No Change", change: "none", uplift };
    } else
      return {
        impact,
        change: parseInt(impact) > 0 ? "increase" : "decrease",
        uplift,
      };
  }
  return { impact: "No Change", change: "none", uplift: null };
};

export const getUplift = function (
  groupMetricData: {
    id: string;
    value: any;
    plus_minus?: number | null;
    uplift?: number;
    uplift_ci_high?: number;
    uplift_ci_low?: number;
  }[],
  compareGroupB: string,
  graphValueFormatter: ({
    isAxisTick,
    value,
    configDataTypes,
    _index,
    decimalAmountShown,
  }: {
    isAxisTick: boolean;
    value: number;
    configDataTypes: DataType[];
    _index?: number;
    decimalAmountShown?: number;
  }) => string | undefined,
  showUplift?: boolean
) {
  const indexB = groupMetricData.findIndex(
    (group: BarMetricData) => group.id === compareGroupB
  );

  if (groupMetricData[indexB]?.uplift) {
    const plusMinus = showUplift ? groupMetricData[indexB]?.plus_minus : null;
    const upliftCiHigh = showUplift
      ? groupMetricData[indexB]?.uplift_ci_high
      : null;
    const upliftCiLow = showUplift
      ? groupMetricData[indexB]?.uplift_ci_low
      : null;
    const uplift =
      graphValueFormatter({
        isAxisTick: false,
        value: groupMetricData[indexB].uplift,
        configDataTypes: ["percentTwoDecimal"],
        decimalAmountShown:
          groupMetricData[indexB].uplift * 100 < 10 ? 2 : undefined,
      }) ?? "0";
    if (parseInt(uplift) === 0) {
      return {
        uplift: "No Change",
        change: "none",
        plusMinus: null,
        upliftCiLow: null,
        upliftCiHigh: null,
      };
    } else
      return {
        uplift,
        change: parseInt(uplift) > 0 ? "increase" : "decrease",
        plusMinus,
        upliftCiHigh,
        upliftCiLow,
      };
  }
  return {
    uplift: "",
    change: "none",
    plusMinus: null,
    upliftCiHigh: null,
    upliftCiLow: null,
  };
};

export const calculateImpact = function (
  valueB: number,
  valueA: number,
  locale?: string
) {
  if (valueB < 0 !== valueA < 0) {
    return "N/A";
  } else
    return `${new Intl.NumberFormat(locale || "en", {
      style: "decimal",
      maximumFractionDigits: 2,
      minimumFractionDigits: 1,
    }).format(((valueB - valueA) / Math.abs(valueA)) * 100)}%`;
};

export const findTooltip = function (
  metricTitleOrConfig: string | GraphConfig,
  metricTooltips: { [key: string]: string },
  experienceCategory: ExperienceCategory,
  metricFilterIndex?: number
) {
  let metricTitle: string;

  if (typeof metricTitleOrConfig === "string") {
    metricTitle = metricTitleOrConfig;
  } else {
    if (
      !metricTitleOrConfig?.title ||
      !Array.isArray(metricTitleOrConfig.title)
    ) {
      return null;
    }
    metricTitle = metricTitleOrConfig.title[metricFilterIndex || 0];
  }

  const foundTooltip = metricTooltips[metricTitle];

  if (foundTooltip) {
    return foundTooltip.replace(
      "test",
      experienceCategory === "experiment" ? "test" : "personalization"
    );
  } else return null;
};

export const getEstImpactCurrencyMetric = function (
  perVisA: number,
  perVisB: number,
  totalTestVisitors: number,
  daysInTest: number
) {
  return Math.round(
    (perVisB - perVisA) * totalTestVisitors * (30 / daysInTest)
  );
};

export const dataMinFormatter = function (dataMin: AxisDomainItem) {
  if (Math.abs(dataMin as number) === 0) {
    return 0;
  } else if (Math.abs(dataMin as number) <= 0.00001) {
    return -0.1;
  } else if (Math.abs(dataMin as number) > 0.00001 && (dataMin as number) < 0) {
    return "auto";
  } else return 0;
};

export const getDataMax = (groupMetricData: BarMetricData[]): number => {
  const valueArray =
    groupMetricData?.map((group) => group.value as number) ?? [];

  return Math.max(...valueArray);
};
export const getDataMin = function (groupMetricData: BarMetricData[]) {
  const valueArray = groupMetricData.map((group) => group.value as number);

  return Math.min(...valueArray);
};

export const getStackedBarRadiusTb = (
  index: number,
  config: GraphConfig
): [number, number, number, number] => {
  if (
    index === config.stackedBarLegendTitles!.length - 1 ||
    config.graphType === GraphType.ClusteredBar
  ) {
    return [3, 3, 0, 0];
  } else return [0, 0, 0, 0];
};

// if it is a clustered graph, do not stack => different stack ids
export const getStackedBarStackId = (index: number, config: GraphConfig) => {
  if (config.graphType === GraphType.ClusteredBar) {
    return `value${index + 1}`;
  } else return "stackId";
};

export const stackedDataMaxFormatter = (
  config: GraphConfig,
  dataMax: number
) => {
  if (config.dataTypes[0] === "percent") {
    return "dataMax";
  } else return "auto";
};

export const getStackedDataMax = (
  groupMetricData: StackedBarMetricData[],
  config: GraphConfig
) => {
  const valueArray = groupMetricData.map((data) =>
    (config.stackedBarLegendTitles || []).reduce(
      (prev, curr) => prev + (data[curr] as number),
      0
    )
  );

  return Math.max(...valueArray);
};

export const getStackedDataValue = (
  config: GraphConfig,
  dataValue1: number | undefined,
  dataValue2: number | undefined
) => {
  if (
    config.showSumStackedLabels ||
    config.graphType === GraphType.ClusteredBar
  ) {
    return dataValue1 ? dataValue1 : 0;
  } else {
    const percentage =
      (dataValue1 ? dataValue1 : 0) /
      ((dataValue1 ? dataValue1 : 0) + (dataValue2 ? dataValue2 : 0));
    return percentage || 0;
  }
};

export const valueAccessor = (
  entry: any,
  index: number,
  config: GraphConfig,
  graphValueFormatter: ({
    isAxisTick,
    value,
    configDataTypes,
    _index,
    decimalAmountShown,
  }: {
    isAxisTick: boolean;
    value: number;
    configDataTypes: DataType[];
    _index?: number;
    decimalAmountShown?: number;
  }) => string | undefined
) => {
  if (index === config.stackedBarLegendTitles!.length - 1) {
    return entry
      ? graphValueFormatter({
          isAxisTick: false,
          value: entry.value[entry.value.length - 1],
          configDataTypes: config.dataTypes,
        })
      : 0;
  } else return null;
};

export const lineGroupColors = [
  theme.palette.primary.main,
  theme.palette.success.main,
  theme.palette.error.main,
  theme.palette.warning.main,
  "purple",
  "black",
];

export const timeSeriesColors = [
  "#2962FF",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#b07aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ac",
];
export const StackedBarImpactColors = ["#172AB6", "#1B46F5"];

export const newHorizStackedBarColors = {
  productGroups: [
    "#FFD4D4",
    "#FF858D",
    "#FE7F2D",
    "#FCCA46",
    "#57A773",
    "#157145",
    "#9D69A3",
    "#9BD1E5",
    "#5171A5",
    "#2962FF",
  ],
  base: [
    "#CCD6FF",
    "#88A6FF",
    "#4880FF",
    "#2962FF",
    "#0034B4",
    "#001469",
    "#000e4a",
  ],
  gray: [IgGrayPrimitives[800], IgGrayPrimitives["A400"]],
};

export const calculateEstImpactTb = (
  config: GraphConfig,
  visitors: number,
  baseValue: number,
  compareValue: number,
  storeCurrencyConfig: { code: string; locale: string },
  testDuration: number,
  pageName: DashboardPageName
): string => {
  const estValue = getEstImpactCurrencyMetric(
    baseValue,
    compareValue,
    visitors,
    testDuration
  );

  if (config.dataTypes[0].includes("currency")) {
    return `${estValue > 0 ? "+" : ""}${currencyFormatter(
      estValue,
      false,
      storeCurrencyConfig
    )} MONTHLY`;
  }

  const suffix =
    pageName === "customMetrics"
      ? "OCCURRENCES"
      : config?.title?.[0]?.includes("Unit")
        ? "UNITS"
        : config?.title?.[0]?.includes("View")
          ? "VIEWS"
          : "ORDERS";

  return `${estValue > 0 ? "+" : ""}${noDecimalNumberFormatter(
    estValue,
    false,
    storeCurrencyConfig
  )} ${suffix} MONTHLY`;
};

export const getFilterMethod = (
  data: BarMetricData[],
  filterMethod: string | undefined
) => {
  const groupData: BarMetricData[] = [];
  const dataCopy = cloneDeep(data);

  dataCopy.forEach((data) => {
    if (data.filterMethod === filterMethod) {
      groupData.push(data);
    }
  });

  return groupData;
};
